<template>
  <div
    class="tw-flex tw-flex-col tw-bg-white tw-bg-lighthouse-light tw-bg-cover tw-h-screen tw-w-full tw-bg-fixed tw-bg-center"
  >
    <t-auth-header />
    <!-- Login Form -->

    <section
      v-if="showLogin"
      class="tw-mb-auto tw-flex tw-flex-col tw-items-center tw-self-center tw-rounded tw-p-9 tw-bg-white tw-bg-opacity-85"
    >
      <cl-heading class="tw-text-center">
        {{ $t("sign-in-title") }}
      </cl-heading>
      <form class="tw-w-full tw-max-w-md" @submit.prevent>
        <cl-form-group class="tw-mb-6 tw-mt-4">
          <cl-form-input
            id="email"
            name="email"
            v-model:value="v$.form.email.$model"
            :placeholder="$t('sign-in-email-placeholder')"
            :size="'large'"
            data-test-id="login-work-email-input"
            :state="!v$.form.email.$dirty ? null : !v$.form.email.$error"
            @blur="handleBlur"
            @on-keyup="cleanErrorMessage"
          />
          <span
            v-if="v$.form.email.$error"
            class="tw-text-base tw-font-normal tw-text-system-error-500"
            >{{ $t("sign-in-email-validation") }}</span
          >
        </cl-form-group>
        <cl-form-group class="tw-mb-6">
          <t-password-input
            id="password"
            name="password"
            v-model:value="v$.form.password.$model"
            :placeholder="$t('password-label')"
            size="large"
            data-test-id="login-work-password-input"
            :state="!v$.form.password.$dirty ? null : !v$.form.password.$error"
            @on-keyup="cleanErrorMessage"
          />
          <span
            v-if="v$.form.password.$error"
            class="tw-text-base tw-font-normal tw-text-system-error-500"
            >{{ $t("sign-in-password") }}</span
          >
          <span
            v-if="v$.samlSsoOnly.$error"
            class="tw-text-base tw-font-normal tw-text-system-error-500"
            >{{ $t("saml-sso-enabled") }}</span
          >
        </cl-form-group>
        <cl-form-group v-if="invalidUser" class="tw-mb-6">
          <span
            id="auth-error-message'"
            class="tw-text-center tw-text-base tw-font-normal tw-text-system-error-500"
          >
            {{ $t("sign-in-mismatch") }}
          </span>
        </cl-form-group>
        <cl-form-group class="tw-mb-6">
          <span class="tw-text-center">
            <router-link
              v-slot="{ href, navigate }"
              :to="{ name: 'auth-password-reset' }"
            >
              <a
                :href="href"
                class="tw-text-sm tw-font-bold tw-text-primary-grey-700 tw-no-underline hover:tw-text-primary-grey-700 hover:tw-underline"
                @click="navigate"
                >{{ $t("sign-in-forgot-password") }}</a
              >
            </router-link>
          </span>
        </cl-form-group>
        <cl-form-group class="tw-items-center">
          <div class="tw-w-[70%]">
            <cl-button
              :variant="'secondary'"
              :size="'large'"
              :type="'submit'"
              :disabled="fetching"
              fullwidth
              data-test-id="sign-in-btn"
              @on-click="submit"
            >
              <cl-spinner
                :loading-text="$t('loading')"
                v-if="fetching"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>{{ $t("sign-in-button") }}</span>
            </cl-button>
          </div>
        </cl-form-group>
        <cl-form-group class="tw-mb-6 tw-items-center">
          <div class="tw-mb-4 tw-flex tw-items-center">
            <span class="tw-text-primary-grey-300 tw-w-[90px] tw-m-2"
              ><hr
            /></span>
            <p class="tw-text-primary-grey-300">
              {{ $t("or-sign-in-with") }}
            </p>
            <span class="tw-text-primary-grey-300 tw-w-[90px] tw-m-2"
              ><hr
            /></span>
          </div>
          <div class="tw-w-[70%]">
            <cl-button
              :variant="'info'"
              :size="'large'"
              :type="'submit'"
              :disabled="fetching"
              fullwidth
              data-test-id="saml-sso-login-btn"
              @click="((showLogin = false), (showSAML = true))"
            >
              <span>{{ $t("saml-sso") }}</span>
            </cl-button>
          </div>
          <div v-if="isAuth0LoginEnabled" class="tw-mt-4 tw-w-[70%]">
            <cl-button
              :variant="'info'"
              :size="'large'"
              :type="'button'"
              :disabled="fetching"
              fullwidth
              data-test-id="auth0-login-btn"
              @on-click="loginWithAuth0"
            >
              <span>{{ $t("sign-in-auth0") }}</span>
            </cl-button>
          </div>
        </cl-form-group>
      </form>
    </section>
    <!-- Login Form End -->
    <!-- Login SAML Form -->
    <section
      v-if="showSAML"
      class="tw-mb-auto tw-flex tw-flex-col tw-items-center tw-self-center tw-rounded tw-p-9 tw-bg-white tw-bg-opacity-85"
    >
      <cl-heading>
        {{ $t("saml-sign-in") }}
      </cl-heading>

      <form class="tw-w-full tw-max-w-md" @submit.prevent>
        <cl-form-group class="tw-mb-6 tw-mt-4">
          <cl-form-input
            id="email"
            name="email"
            v-model:value="v$.form.email.$model"
            :placeholder="$t('sign-in-email-placeholder')"
            data-test-id="saml-sso-login-work-email-input"
            :size="'large'"
            :state="!v$.form.email.$dirty ? null : !v$.form.email.$error"
            @on-keyup="cleanErrorMessage"
          />
          <div
            v-if="samlError?.length"
            class="tw-text-base tw-font-normal tw-text-system-error-500"
          >
            <div v-for="(err, index) in samlError" :key="index">
              <span>{{ err }}</span>
            </div>
          </div>
        </cl-form-group>
        <cl-form-group class="tw-m-12 tw-items-center">
          <div class="tw-w-[70%]">
            <cl-button
              :variant="'info'"
              :size="'large'"
              :type="'submit'"
              :disabled="fetching"
              fullwidth
              data-test-id="saml-sso-login-btn-continue"
              @on-click="samlSSO"
            >
              <cl-spinner
                :loading-text="$t('loading')"
                v-if="fetching"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>{{ $t("continue") }}</span>
            </cl-button>
          </div>
        </cl-form-group>
        <div
          class="tw-flex tw-justify-center tw-cursor-pointer"
          @click="((showLogin = true), (showSAML = false))"
        >
          <div
            data-test-id="saml-sso-back-to-login-btn"
            class="tw-flex tw-gap-2"
          >
            <cb-icon-arrow-up class="tw-rotate-[270deg]" />
            {{ $t("saml-sso-back") }}
          </div>
        </div>
      </form>
    </section>

    <!-- Login 2fa Form -->
    <section
      v-if="show2FA"
      id="login-2fa"
      class="tw-mb-auto tw-flex tw-flex-col tw-items-center tw-self-center tw-text-center tw-rounded tw-p-9 tw-bg-white tw-bg-opacity-85"
    >
      <cl-heading>
        {{ $t("2fa") }}
      </cl-heading>
      <cl-p v-if="show2FA && !showBackupCode">
        {{ $t("2fa-sign-in") }}
      </cl-p>
      <cl-p v-if="show2FA && showBackupCode">
        {{ $t("2fa-enter-recovery-code") }}
      </cl-p>
      <form class="tw-w-full tw-max-w-md" @submit.prevent>
        <cl-form-group v-if="!showBackupCode" class="tw-mb-6 tw-mt-4">
          <div class="tw-flex tw-justify-center">
            <cl-form-input
              v-for="(el, index) in twoFactorInputs"
              :id="el"
              :name="el"
              :key="el"
              :ref="el"
              v-model:value="twoFactorCode[el as keyof TwoFactorCode] as string"
              :maxlength="1"
              autocomplete="false"
              class="tw-mx-1 tw-max-w-[40px]"
              :tabindex="index + 1"
              :size="'large'"
              @on-keyup="on2faInput"
              @on-paste="on2faPaste"
              :autofocus="index === 0"
            />
          </div>
        </cl-form-group>
        <cl-form-group
          v-if="showBackupCode"
          id="backup-2fa-code"
          class="tw-mb-6 tw-mt-4"
        >
          <cl-form-input
            id="backup-code"
            name="backup-code"
            v-model:value="backupCode"
            tabindex="1"
            autocomplete="false"
            :placeholder="$t('recovery-code')"
            :size="'large'"
            @on-keydown="cleanErrorMessage"
          />
        </cl-form-group>
        <cl-form-group v-if="invalidUser2fa" class="tw-mb-6">
          <span
            class="tw-text-center tw-text-base tw-font-normal tw-text-system-error-500"
            >{{ $t("2fa-incorrect-recovery-code") }}</span
          >
        </cl-form-group>
        <cl-form-group class="tw-mb-6">
          <cl-p>
            {{ $t("2fa-not-working") }}
            <a class="tw-text-primary-grey-700" href="#" @click="cancel2fa">{{
              $t("try-again")
            }}</a>
          </cl-p>
          <cl-p v-if="show2FA && !showBackupCode">
            {{ $t("2fa-or") }}
            <a
              class="tw-text-primary-grey-700"
              href="#"
              @click="enableBackupCode"
              >{{ $t("2fa-try-another-way") }}</a
            >
          </cl-p>
        </cl-form-group>
        <cl-form-group class="tw-mb-6 tw-items-center">
          <div class="tw-w-[70%]">
            <cl-button
              :variant="'secondary'"
              :size="'large'"
              :type="'submit'"
              :disabled="fetching"
              fullwidth
              data-test-id="login-btn"
              @on-click="submit"
            >
              <cl-spinner
                :loading-text="$t('loading')"
                v-if="fetching"
                :size="'small'"
                class="tw-mr-2"
              />
              <span>{{ $t("sign-in-title") }}</span>
            </cl-button>
          </div>
        </cl-form-group>

        <cl-form-group class="tw-mb-6">
          <cl-p>
            {{ $t("sign-in-agree-thq-terms") }}
            <a
              href="https://www.titanhq.com/software-licence-agreement/"
              class="tw-text-primary-grey-700"
              target="_blank"
              >{{ $t("sign-in-thq-terms-of-service") }}
            </a>
            {{ $t("add-exemption-and") }}
            <a
              href="https://www.titanhq.com/about/privacy-policy/"
              class="tw-text-primary-grey-700"
              target="_blank"
              >Privacy Policy</a
            >
          </cl-p>
        </cl-form-group>
      </form>
    </section>
    <!-- Login 2fa Form End -->
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onBeforeMount } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthenticationStore } from "@/store/authentication.store";
import { useOrganisationsStore } from "@/store/organisations.store";
import { buildOrgUrl } from "@/utils/navigation/orgUrl";
import { useVuelidate } from "@vuelidate/core";
import { required, email } from "@vuelidate/validators";
import { useI18n } from "vue-i18n";

import TAuthHeader from "@/components/TAuthHeader/index.vue";
import { ACCOUNT_TYPE } from "@/constants";
import TPasswordInput from "@/components/TPasswordInput";

import { useAuthorisationStrategy } from "@/authorisation";
import { useAuth0SessionStore } from "@/store/auth0Session.store";
import { useFlag } from "@/composables/useFlags/useFlag";
import { useToastStore } from "@/components/TToasts/toast.store.ts";
import { $userPrefs } from "@/plugins/userPrefs";
import { isAxiosError } from "axios";
import { getApiClientUrl } from "@/config/runtimeConfig";

type FormData = {
  email: string;
  password: string;
};

type TwoFactorCode = {
  code1: string;
  code2: string;
  code3: string;
  code4: string;
  code5: string;
  code6: string;
};

type SsoResponse = {
  data: {
    sso: boolean;
    default_login_enabled: boolean;
  };
};

type AuthToken = {
  access_token: string;
};

type AuthenticationState = {
  selectedAccount?: {
    type_name: string;
  };
  token: AuthToken | null;
};

// Add missing DOMAIN type that was in the original code
const ACCOUNT_TYPE_EXTENDED = {
  ...ACCOUNT_TYPE,
  DOMAIN: "Domain",
  USER: "User",
} as const;

const authStore = useAuthenticationStore();
const organisationsStore = useOrganisationsStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { authoriseOnSignIn, clearAuthorisationCache } =
  useAuthorisationStrategy();
const toastStore = useToastStore();
const { isFeatureAvailable: isAuth0LoginEnabled } = useFlag("AUTH0_LOGIN");

const fetching = ref<boolean>(false);
const invalidUser = ref<boolean>(false);
const invalidUser2fa = ref<boolean>(false);
const showLogin = ref<boolean>(true);
const show2FA = ref<boolean>(false);
const showSAML = ref<boolean>(false);
const showBackupCode = ref<boolean>(false);
const samlError = ref<string[] | null>(null);
const isSsoDefaultLoginEnabled = ref<boolean>(false);
const isSsoConfigured = ref<boolean>(false);
const backupCode = ref<string>("");

/**
 * Generates a short, secure, URL-safe state token.
 *
 * We use 12 random bytes converted to Base64URL and truncated to 6 characters.
 * Truncating is safe because we treat this as an opaque unique token, not data to decode.
 *
 * Why 6 characters?
 * The backend often passes this in the SAML `RelayState` parameter along with the username.
 * Some Identity Providers limit `RelayState` to 80 characters.
 * u=<email>&s=<state>
 * A short state token maximizes space for long email addresses.
 * 6 chars = 64^6 = ~68 billion combinations, sufficient for transient session state.
 */
const generateStateToken = (): string => {
  const array = new Uint8Array(12);
  window.crypto.getRandomValues(array);
  const binary = Array.from(array, (b) => String.fromCharCode(b)).join("");
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "")
    .substring(0, 6);
};

const form = reactive<FormData>({
  email: "",
  password: "",
});

const twoFactorCode = reactive<TwoFactorCode>({
  code1: "",
  code2: "",
  code3: "",
  code4: "",
  code5: "",
  code6: "",
});

const authentication = computed<AuthenticationState>(
  () => authStore.$state as unknown as AuthenticationState,
);

const redirectTarget = computed<string | null>(() => {
  const redirect = route.query.redirect;
  return typeof redirect === "string" ? redirect : null;
});

const ensureScopedRedirect = (
  target: string,
  orgUuid: string | null,
): string | null => {
  if (!orgUuid) return null;

  const match = target.match(
    /^\/(msp|customer|system)(?:\/([^/?#]+))?(?<rest>\/[^?#]*)?(?<query>\?[^#]*)?(?<hash>#.*)?$/i,
  );
  if (!match) return target;

  const orgType = match[1];
  const maybeUuid = match[2];

  // If target already has a UUID-looking segment (36 chars with dashes), keep as-is
  if (maybeUuid && maybeUuid.length === 36) return target;

  const rest = match.groups?.rest || "";
  const suffix = rest.startsWith("/") ? rest.slice(1) : rest;
  const rebuilt = buildOrgUrl(orgType.toLowerCase(), orgUuid, suffix);
  const query = match.groups?.query || "";
  const hash = match.groups?.hash || "";
  return `${rebuilt}${query}${hash}`;
};

const enabledSSO = (user: { email: string }) =>
  authStore.ssoEnabled(user.email);

const requestToken = (user: {
  username: string;
  password: string;
  code: string;
}) => authStore.requestToken(user);

const getOrgDetails = (uuid: string) => organisationsStore.showOrg({ uuid });

const clearAuthCache = () => authStore.clearAuthCache();

const samlSsoConfigurationCheck = {
  isValid() {
    return !(
      isSsoConfigured.value === true && isSsoDefaultLoginEnabled.value === false
    );
  },
};

const rules = {
  form: {
    email: { required, email },
    password: { required },
  },
  samlSsoOnly: samlSsoConfigurationCheck,
};

const v$ = useVuelidate(rules, { form, samlSsoOnly: {} });

const isTokenValid = computed<boolean>(() => {
  const token = $userPrefs.getCurrentToken();

  if (!token) {
    return false;
  }

  const splitToken = atob(token.split(".")[1]);
  const parsedToken = JSON.parse(splitToken);

  return +parsedToken.exp * 1000 > Date.now();
});

const twoFactorInputs = computed<string[]>(() => {
  return new Array(6).fill(null).map((_el, index) => `code${index + 1}`);
});

const consolidated2faCode = computed<string | null>(() => {
  if (showBackupCode.value) {
    return backupCode.value || "";
  }

  const codeValues = Object.values(twoFactorCode);

  if (codeValues.some((value) => !value)) {
    return "";
  }

  return codeValues.reduce((acc, next) => (acc || "") + (next || ""), "");
});

const cleanErrorMessage = (): void => {
  invalidUser.value = false;
  invalidUser2fa.value = false;
};

const registerAppCues = async (userInfo: {
  products: string;
  id: string;
  accountname: string;
  email: string;
  role: string;
  organisation: string;
  organisation_uuid: string;
  level: string;
  parent_name: string;
  parent_uuid: string;
}): Promise<void> => {
  const userId = userInfo?.id || "unknown";
  const userName = userInfo?.accountname || "unknown";
  const userEmail = userInfo?.email || "unknown";
  const userRole = userInfo?.role || "unknown";
  const orgName = userInfo?.organisation || "unknown";
  const products = userInfo?.products || "unknown";
  const userLevel = userInfo?.level || "unknown";
  const parentName = userInfo?.parent_name || "unknown";
  const parentUuid = userInfo?.parent_uuid || "unknown";

  try {
    // @ts-ignore
    window.Appcues.identify(userId, {
      name: userName,
      companyName: orgName,
      email: userEmail,
      role: userRole,
      products: products,
      userLevel: userLevel,
      parentName: parentName,
      parentUuid: parentUuid,
    });
  } catch {
    return;
  }
};

const routeBasedOnRole = (): void => {
  const type = authentication.value.selectedAccount?.type_name;
  const orgUuid =
    (authentication.value.selectedAccount as { uuid?: string } | undefined)
      ?.uuid ||
    authStore.authUser?.organisation_uuid ||
    authStore.authUser?.organisationUuid ||
    null;

  if (!orgUuid) return;

  if (type === ACCOUNT_TYPE.SYSTEM) {
    router.push(buildOrgUrl("system", orgUuid));
    return;
  }

  if (type === ACCOUNT_TYPE.SALES) {
    router.push(buildOrgUrl("system", orgUuid));
    return;
  }

  if (type === ACCOUNT_TYPE.MSP) {
    router.push(buildOrgUrl("msp", orgUuid));
    return;
  }

  if (type === ACCOUNT_TYPE.CUSTOMER) {
    router.push(buildOrgUrl("customer", orgUuid));
    return;
  }

  if (type === ACCOUNT_TYPE_EXTENDED.DOMAIN) {
    router.push(buildOrgUrl("system", orgUuid));
    return;
  }

  router.push(buildOrgUrl("system", orgUuid));
};

const hasAccessToOrganisation = (
  userType: string,
  userOrgUuid: string | null,
  targetOrgUuid: string,
): boolean => {
  // System admins have access to all organizations
  if (userType === ACCOUNT_TYPE.SYSTEM) {
    return true;
  }

  // For other roles, check if they belong to the target organization
  return userOrgUuid === targetOrgUuid;
};

const goAfterLogin = async (): Promise<void> => {
  const orgUuid =
    (authentication.value.selectedAccount as { uuid?: string } | undefined)
      ?.uuid ||
    authStore.authUser?.organisation_uuid ||
    authStore.authUser?.organisationUuid ||
    null;

  if (authStore.authUser?.organisation_uuid !== undefined) {
    try {
      const details = (await getOrgDetails(
        authStore.authUser?.organisation_uuid,
      ))!;
      await registerAppCues({
        ...authStore.authUser,
        parent_name: details.data?.parent_name,
        parent_uuid: details.data?.parent_uuid,
      } as Parameters<typeof registerAppCues>[0]);
    } catch {
      return;
    }
  }

  if (redirectTarget.value) {
    const userType = authentication.value.selectedAccount?.type_name;

    // For system admins, use the original redirect URL without modification
    if (userType === ACCOUNT_TYPE.SYSTEM) {
      router.replace(redirectTarget.value).catch(() => {});
      return;
    }

    // For other users, ensure scoped redirect and validate access
    const maybeScoped = ensureScopedRedirect(redirectTarget.value, orgUuid);
    if (maybeScoped) {
      // Extract org UUID from redirect URL to validate access
      const redirectOrgMatch = maybeScoped.match(
        /\/(msp|customer|system)\/([^/?#]+)/,
      );
      if (redirectOrgMatch) {
        const redirectOrgUuid = redirectOrgMatch[2];

        // Check if user has access to the redirect organization
        if (
          !hasAccessToOrganisation(userType || "", orgUuid, redirectOrgUuid)
        ) {
          // User doesn't have access to redirect org, go to their default
          routeBasedOnRole();
          return;
        }
      }
      router.replace(maybeScoped).catch(() => {});
      return;
    }
  }

  routeBasedOnRole();
};

const on2faInput = (e: KeyboardEvent): void => {
  cleanErrorMessage();

  const code = e.keyCode || e.which;

  if (code === 9) {
    return;
  }

  const target = e.target as HTMLInputElement;

  if (code === 8 || code === 37) {
    if (target.id !== "code1") {
      (target.previousSibling as HTMLInputElement)?.focus();
    }
  } else {
    if (target.id !== "code6") {
      (target.nextSibling as HTMLInputElement)?.focus();
    }
  }
};

const on2faPaste = (ev: ClipboardEvent): void => {
  const clip = ev.clipboardData?.getData("text").trim() || "";

  if (!/\d{6}/.test(clip)) {
    return ev.preventDefault();
  }

  const symbols = [...clip];
  twoFactorCode.code1 = symbols[0];
  twoFactorCode.code2 = symbols[1];
  twoFactorCode.code3 = symbols[2];
  twoFactorCode.code4 = symbols[3];
  twoFactorCode.code5 = symbols[4];
  twoFactorCode.code6 = symbols[5];

  // Focus last input - would need ref to work properly
  // refs.code6?.[0]?.focus();
};

const handleBlur = async (): Promise<void> => {
  const isValid = await v$.value.form.email.$validate();
  if (!isValid) {
    return;
  }
  samlError.value = [];
  let isSSO: SsoResponse | null = null;

  if (form.email) {
    isSSO = await enabledSSO({
      email: form.email,
    });

    if (!isSSO) {
      return;
    }

    isSsoDefaultLoginEnabled.value = isSSO.data.default_login_enabled;
    isSsoConfigured.value = isSSO.data.sso;

    if (isSSO.data.sso && !isSSO.data.default_login_enabled) {
      showSAML.value = true;
      showLogin.value = false;
    }
  }
};

const cancel2fa = (): void => {
  cleanErrorMessage();

  showBackupCode.value = false;
  show2FA.value = false;
  showLogin.value = true;

  backupCode.value = "";
  twoFactorCode.code1 = "";
  twoFactorCode.code2 = "";
  twoFactorCode.code3 = "";
  twoFactorCode.code4 = "";
  twoFactorCode.code5 = "";
  twoFactorCode.code6 = "";
};

const enableBackupCode = (): void => {
  showBackupCode.value = true;
  invalidUser2fa.value = false;
};

const samlSSO = async (): Promise<void> => {
  showSAML.value = true;
  showLogin.value = false;
  const isValid = await v$.value.$validate();
  fetching.value = true;
  let isSSO: SsoResponse | null = null;

  if (!showSAML.value && !isValid) {
    return;
  }

  try {
    if (form.email) {
      isSSO = await enabledSSO({
        email: form.email,
      });
    }
  } catch (error: unknown) {
    if (!isAxiosError(error)) {
      return;
    }

    if (error.response?.data.error === "The selected user doesn't exist") {
      samlError.value = [
        "Your email is not set up to use SAML SSO.",
        "Please contact your administrator for assistance.",
      ];
    } else {
      samlError.value = [error.response?.data.error];
    }
  } finally {
    fetching.value = false;
    if (isSSO?.data?.sso) {
      const state = generateStateToken();

      if (redirectTarget.value) {
        sessionStorage.setItem(`auth_redirect_${state}`, redirectTarget.value);
      }
      globalThis.location.href = `${getApiClientUrl()}/v1/idp/sso/saml/login/${encodeURIComponent(form.email || "")}?state=${state}`;
    } else {
      samlError.value = [
        "Your email is not set up to use SAML SSO.",
        "Please contact your administrator for assistance.",
      ];
    }
  }
};

const loginWithAuth0 = (): void => {
  // Guard the method itself, not just the button: never initiate an Auth0
  // redirect when the feature is disabled.
  if (!isAuth0LoginEnabled.value) return;

  // Full-page redirect to the Auth0 RWA login endpoint. Return to the intended
  // destination when present; never to the sign-in path itself (avoids a loop).
  const returnPath = redirectTarget.value || "/";
  const invitation =
    typeof route.query.invitation === "string" ? route.query.invitation : null;
  const organization =
    typeof route.query.organization === "string"
      ? route.query.organization
      : null;

  try {
    // Resolved lazily: the store is only needed on click (keeps setup free of a
    // Pinia dependency for the rest of the sign-in flow).
    useAuth0SessionStore().login(returnPath, { invitation, organization });
  } catch {
    // Failures that come back FROM Auth0 redirect to /auth/sign-in?error=<key>
    // and are surfaced by the route.query.error handler in onBeforeMount. This
    // only covers the rare case where initiating the redirect itself fails.
    toastStore.displayToast({
      title: t("error"),
      message: t("auth0-sign-in-error"),
      duration: 5000,
      variant: "danger",
      testId: "auth0-sign-in-toast-error",
    });
  }
};

const submit = async (): Promise<void> => {
  const isValid = await v$.value.$validate();

  if (!show2FA.value && !isValid) {
    return;
  }

  try {
    fetching.value = true;

    await requestToken({
      username: form.email!,
      password: form.password!,
      code: consolidated2faCode.value || "",
    });

    const token = authentication.value.token;

    if (token) {
      const splitToken = atob(token.access_token.split(".")[1]);
      const parsedToken = JSON.parse(splitToken);

      $userPrefs.updateUserPrefs({
        token: token.access_token,
        role: undefined,
        twoFactorAuthEnabled: parsedToken["enabled_2fa"],
      });

      await authoriseOnSignIn();

      await goAfterLogin();
    }
  } catch (err: unknown) {
    clearAuthorisationCache();
    clearAuthCache();

    if (!isAxiosError(err)) return;

    if (
      err.response?.status === 422 &&
      err.response.data.fields[0].field === "code"
    ) {
      if (show2FA.value) {
        invalidUser2fa.value = true;
        twoFactorCode.code1 = "";
        twoFactorCode.code2 = "";
        twoFactorCode.code3 = "";
        twoFactorCode.code4 = "";
        twoFactorCode.code5 = "";
        twoFactorCode.code6 = "";

        toastStore.displayToast({
          title: t("error"),
          message: t("2fa-incorrect-recovery-code"),
          duration: 5000,
          variant: "danger",
          testId: "sign-in-2fa-toast-error",
        });

        return;
      } else {
        show2FA.value = true;
        showLogin.value = false;

        return;
      }
    }

    if (err.response?.status === 401 && show2FA.value) {
      invalidUser2fa.value = true;
      return;
    }

    if (err.response?.status === 401) {
      invalidUser.value = true;
      return;
    }
  } finally {
    fetching.value = false;
  }
};

onBeforeMount(() => {
  if (isTokenValid.value) {
    goAfterLogin();
  }

  if (route.query.error) {
    toastStore.displayToast({
      title: t("error"),
      message: t(route.query.error as string),
      duration: 5000,
      variant: "danger",
      testId: "auth-sign-in-route-toast-error",
    });
  }
});
</script>
